<template>
  <q-select
    ref="workflow"
    v-model="targetWorkflow"
    display-value-html
    :class="{ 'q-mb-md': isMobileView }"
    :options="workflowOptions"
    :label="label||$t('editProgram.selectWorkflowLabel')"
    outlined
    :bottom-slots="bottomSlots"
    :hide-bottom-space="hideBottomSpace"
    emit-value
    map-options
    :hint="$t('editProgram.selectWorkflowHint')"
    :hide-hint="!isMobileView"
    clearable
    class="col-12"
    @clear="onClearWorkflowAdjustments"
    @update:model-value="onClearWorkflowAdjustments"
  >
    <template
      v-if="hasWorkflowAdjustments"
      #prepend
    >
      <q-icon
        name="new_releases"
        color="primary"
      >
        <q-tooltip>
          {{ adjustmentsTooltip }}
        </q-tooltip>
      </q-icon>
    </template>
    <template
      v-if="targetWorkflow"
      #append
    >
      <q-btn
        round
        flat
        icon="tune"
        @click.stop="onAdjustWorkflow({value: targetWorkflow})"
      >
        <q-tooltip>
          {{ $t('programs.workflows.editWorkflow.textTitleAdjustSettings') }}
        </q-tooltip>
      </q-btn>
    </template>
    <template #option="scope">
      <q-item
        v-if="scope.opt.isHeading"
        class="text-caption text-bold text-white bg-primary type-heading"
      >
        <q-item-section>
          <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
          <q-item-label v-html="scope.opt.label" />
        </q-item-section>
      </q-item>
      <q-item
        v-else
        v-bind="scope.itemProps"
      >
        <q-item-section>
          <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
          <q-item-label v-html="scope.opt.label" />
        </q-item-section>
        <q-item-section side>
          <q-btn
            round
            flat
            icon="tune"
            @click.stop="onAdjustWorkflow(scope.opt)"
          >
            <q-tooltip>
              {{ $t('common.buttonPreview') }}
            </q-tooltip>
          </q-btn>
        </q-item-section>
      </q-item>
    </template>
    <template
      v-if="addWorkflow"
      #after-options
    >
      <q-separator />
      <q-item
        clickable
        @click="onAddWorkflow"
      >
        <q-item-section>
          <q-item-label>
            {{ $t('editProgram.addWorkflowLabel') }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <template #no-option>
      <q-item>
        <q-item-section class="text-grey">
          {{ $t('editProgram.itemNoWorkflows') }}
        </q-item-section>
      </q-item>
      <q-separator v-if="addWorkflow" />
      <q-item
        v-if="addWorkflow"
        clickable
        @click="onAddWorkflow"
      >
        <q-item-section>
          <q-item-label>
            {{ $t('editProgram.addWorkflowLabel') }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>
<script>
import { workflowTypes } from 'boot/globals';
import EditWorkflow from 'components/Programs/Workflows/EditWorkflow';
import { chain, get, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import WorkflowsDB from 'src/fb/workflows-db';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';

export default {
  name: 'WorkflowSelect',

  mixins: [Interactions],

  props: {
    modelValue: {
      type: String,
      required: false,
      default: '',
    },

    workflowAdjustments: {
      type: Object,
      default: () => ({}),
    },

    label: {
      type: String,
      default: null,
    },

    metric: {
      type: String,
      default: null,
    },

    loadedWorkflows: {
      type: Array,
      required: true,
    },

    adjustmentsTooltip: {
      type: String,
      default: null,
    },

    contextLabel: {
      type: String,
      default: '',
    },

    disableAdjustment: {
      type: Boolean,
      default: false,
    },

    addWorkflow: {
      type: Boolean,
      default: false,
    },

    bottomSlots: {
      type: Boolean,
      default: false,
    },

    hideBottomSpace: {
      type: Boolean,
      default: false,
    },

    packageId: {
      type: String,
      default: null,
    },

  },

  emits: ['update:modelValue', 'adjust-workflow', 'reload-workflows'],

  data() {
    return {
    };
  },

  computed: {
    ...mapState(auth, ['isManager']),

    isMobileView() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    }, // isMobileView()

    hasWorkflowAdjustments() {
      return !isEmpty(this.workflowAdjustments);
    },

    workflowOptions() {
      const validWorkflowTypes = chain(workflowTypes)
        .pickBy((workflowType) => (workflowType.metric === this.metric || workflowType.showAlways))
        .keys()
        .value();

      const workflows = chain(this.loadedWorkflows)
        .filter((workflow) => (validWorkflowTypes.includes(workflow.type || 'accuracyIncrease')))
        .map((workflow) => ({
          value: workflow.id,
          label: workflow.workflowName,
          type: workflow.type || 'accuracyIncrease',
          isHeading: false,
        }))
        .value();

      const workflowOptions = chain(workflows)
        .map('type')
        .uniq()
        .map((workflowType) => ({
          value: workflowType,
          label: get(workflowTypes, [workflowType, 'label']),
          type: workflowType,
          isHeading: true,
          sortOrder: get(workflowTypes, [workflowType, 'sortOrder']),
        }))
        .sortBy('sortOrder')
        .flatMap((workflowHeading) => ([
          workflowHeading,
          ...workflows.filter((workflow) => (workflow.type === workflowHeading.type)),
        ]))
        .value();

      if (this.modelValue && !workflows.find((workflow) => (workflow.value === this.modelValue))) {
        const label = this.loadedWorkflows.find((workflow) => (workflow.id === this.modelValue))?.workflowName;
        workflowOptions.unshift({
          value: this.modelValue,
          label,
          type: 'selected',
          isHeading: false,
        });
      }

      return workflowOptions;
    }, // workflowOptions()

    targetWorkflow: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit('update:modelValue', v);
      },
    }, // targetWorkflow()
  },

  methods: {

    onAddWorkflow() {
      const items = chain(workflowTypes)
        .pickBy((workflowType) => (workflowType.metric === this.metric || workflowType.showAlways))
        .map((workflowType, workflowTypeId) => ({
          value: workflowTypeId,
          label: workflowType.label,
        }))
        .value();

      this.$refs.workflow?.hidePopup();

      this.$q.dialog({
        title: this.$t('editProgram.addWorkflowLabel'),
        message: this.$t('editProgram.addWorkflowCaption'),
        ok: {
          label: this.$t('common.buttonSubmit'),
          flat: true,
        },
        options: {
          type: 'radio',
          model: items?.[0]?.value,
          isValid: (val) => !!val,
          items,
        },
        persistent: true,
        cancel: true,
      }).onOk((selectedType) => {
        this.addNewWorkflow(selectedType);
      });
    },

    addNewWorkflow(type) {
      this.openComponentDialog(
        EditWorkflow,
        {
          workflow: this.packageId ? {
            clinic: null,
            package: this.packageId,
            type,
          } : {
            type,
          },
          persistent: true,
        },
        ({ workflowId }) => {
          this.$emit('adjust-workflow', null);
          this.$emit('reload-workflows');
          this.$emit('update:modelValue', workflowId);
          this.$refs.workflow?.hidePopup();
        },
      );
    }, // onAddWorkflow()

    async onAdjustWorkflow(workflowInfo) {
      const workflowValue = get(workflowInfo, 'value');

      // In a rare case when a workflow was deleted while used somewhere there will be a <span>
      // with some info instead of an ID
      if (workflowValue?.includes('</span>')) {
        return;
      }

      const workflowsDb = new WorkflowsDB();
      let workflow = null;
      try {
        this.$q.loading.show();
        workflow = await workflowsDb.read(workflowValue);
      } finally {
        this.$q.loading.hide();
      }
      if (!workflow) {
        return;
      }

      const readonly = this.disableAdjustment;
      const extendWorkflow = true;

      this.openComponentDialog(
        EditWorkflow,
        {
          workflow,
          readonly,
          extendWorkflow,
          extendWorkflowDetails: this.workflowAdjustments,
          extendOrigin: this.contextLabel,
          persistent: true,
        },
        ({ workflowId, workflowAdjustments }) => {
          this.targetWorkflow = workflowId;
          this.$emit('adjust-workflow', workflowAdjustments);
          this.$refs.workflow.hidePopup();
        },
      );
    }, // onAdjustWorkflow()

    onClearWorkflowAdjustments() {
      this.$emit('adjust-workflow', null);
    }, // onClearWorkflowAdjustments()

  },
};

</script>
<style lang="scss" scoped>
.type-heading {
    padding-top:4px!important;
    padding-bottom:4px!important;
    min-height:0!important;
}
</style>
